// ─── Painel Dados: consulta em segundo plano ───────────────────────────────
// O pipeline de IA agora roda desacoplado do request no backend (job com id
// próprio). Este módulo é a cola do lado do cliente: iniciar um job, re-anexar
// ao stream dele depois de trocar de painel / dar F5, cancelar de verdade
// (parando o trabalho no servidor, não só escondendo a tela) e aceitar o
// escalonamento de modelo.
//
// MANTER IDÊNTICO ao gêmeo em fin-twms/frontend/code/src/lib/oracleAiJob.ts.
// Os dois painéis (InfrastructureDBPanel no crm, DadosPanel no fin) já são
// cópias quase iguais e vêm divergindo; concentrar a lógica nova aqui é o que
// impede a divergência de crescer.

export type SseHandlers = {
  onEvent: (event: string, payload: Record<string, unknown>) => void
  /** Chamado a cada leitura; devolva false para abortar o consumo do stream. */
  aindaValido?: () => boolean
}

/**
 * Parser SSE manual. Não dá para usar EventSource: ele não faz POST nem manda
 * header Authorization. Cada evento é "event: NOME\ndata: JSON\n\n" e a leitura
 * pode partir um evento ao meio entre chunks, por isso o buffer só processa
 * blocos completos terminados em \n\n.
 */
export async function consumeSse(body: ReadableStream<Uint8Array>, h: SseHandlers): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    if (h.aindaValido && !h.aindaValido()) break
    buffer += decoder.decode(value, { stream: true })
    let sepIdx: number
    while ((sepIdx = buffer.indexOf('\n\n')) >= 0) {
      const rawEvent = buffer.slice(0, sepIdx)
      buffer = buffer.slice(sepIdx + 2)
      let eventName = 'message'
      let dataLine = ''
      for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim()
        else if (line.startsWith('data:')) dataLine += line.slice(5).trim()
      }
      if (!dataLine) continue
      let payload: Record<string, unknown>
      try { payload = JSON.parse(dataLine) } catch { continue }
      h.onEvent(eventName, payload)
    }
  }
}

// ─── Referência local do job ───────────────────────────────────────────────
// localStorage e não sessionStorage: sessionStorage morre ao fechar a aba, e o
// requisito inclui "sai e volta". Chaveado por usuário porque a máquina é
// compartilhada — sem isso o histórico de um vaza para o próximo login.

export type JobRef = { jobId: string; startedAt: number; prompt: string; lastSeq: number }

const chaveJob = (userId: string) => `oracle.ai.job.${userId}`
const chaveMsgs = (userId: string) => `oracle.ai.messages.${userId}`

export function salvaJobRef(userId: string, ref: JobRef | null): void {
  try {
    if (!ref) localStorage.removeItem(chaveJob(userId))
    else localStorage.setItem(chaveJob(userId), JSON.stringify(ref))
  } catch { /* quota cheia / modo privado: seguir sem persistência local */ }
}

export function carregaJobRef(userId: string): JobRef | null {
  try {
    const s = localStorage.getItem(chaveJob(userId))
    return s ? JSON.parse(s) as JobRef : null
  } catch { return null }
}

/** Teto de ~30 turnos: o localStorage tem 5MB e o resultado de cada turno é gordo. */
export function salvaMensagens(userId: string, msgs: unknown[]): void {
  try {
    localStorage.setItem(chaveMsgs(userId), JSON.stringify(msgs.slice(-30)))
  } catch { /* idem */ }
}

export function carregaMensagens<T>(userId: string): T[] {
  try {
    const s = localStorage.getItem(chaveMsgs(userId))
    return s ? JSON.parse(s) as T[] : []
  } catch { return [] }
}

export function limpaPersistencia(userId: string): void {
  try {
    localStorage.removeItem(chaveJob(userId))
    localStorage.removeItem(chaveMsgs(userId))
  } catch { /* */ }
}

// ─── Chamadas ao backend ───────────────────────────────────────────────────
// authenticatedFetch é injetado para este módulo não depender do AuthContext
// (os dois apps têm o seu, com caminhos de import diferentes).

type Fetcher = (url: string, init?: RequestInit) => Promise<Response>

export type JobResumo = {
  id: string
  status: string
  stage?: string
  stage_label?: string
  progress?: number
  tier?: number
  prompt?: string
  last_seq?: number
  started_at?: string
  acked_at?: string | null
}

export async function listaJobsAtivos(f: Fetcher): Promise<JobResumo[]> {
  try {
    const r = await f('/api/oracle/ai-jobs?status=running')
    if (!r.ok) return []
    const d = await r.json() as { jobs?: JobResumo[] }
    return d.jobs || []
  } catch { return [] }
}

/** Re-anexa ao stream de um job. fromSeq evita reprocessar o que já foi visto. */
export async function anexaJob(f: Fetcher, jobId: string, fromSeq = 0): Promise<Response> {
  return f(`/api/oracle/ai-jobs/${jobId}/stream?fromSeq=${fromSeq}`, { timeout: 600000 } as RequestInit)
}

export async function buscaJob(f: Fetcher, jobId: string, fromSeq = 0): Promise<Record<string, unknown> | null> {
  try {
    const r = await f(`/api/oracle/ai-jobs/${jobId}?fromSeq=${fromSeq}`)
    if (!r.ok) return null
    return await r.json() as Record<string, unknown>
  } catch { return null }
}

/** Cancelar de verdade: o servidor aborta a chamada em voo ao sidecar. */
export async function cancelaJob(f: Fetcher, jobId: string): Promise<void> {
  try { await f(`/api/oracle/ai-jobs/${jobId}/cancel`, { method: 'POST' }) } catch { /* */ }
}

export async function marcaJobVisto(f: Fetcher, jobId: string): Promise<void> {
  try { await f(`/api/oracle/ai-jobs/${jobId}/ack`, { method: 'POST' }) } catch { /* */ }
}

export type OfertaEscalonamento = {
  jobId: string
  fromTier: string
  nextTier: string
  nextTierLabel: string
  reason: string
  struggleScore: number
  roundsUsed: number
  quotaRemaining: number
}

/**
 * Aceita o modo ultra. O cliente manda só o jobId — nunca o tier — para não
 * poder pedir o modelo mais caro direto; quem decide o próximo degrau, revalida
 * a permissão e confere a cota do dia é o servidor.
 */
export async function escalaJob(f: Fetcher, jobId: string): Promise<{ ok: boolean; erro?: string; tier?: number }> {
  try {
    const r = await f('/api/oracle/ai-escalate', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ jobId }),
    })
    const d = await r.json() as { success?: boolean; error?: string; tier?: number }
    if (!r.ok || !d.success) return { ok: false, erro: d.error || 'Não foi possível escalar.' }
    return { ok: true, tier: d.tier }
  } catch (e) {
    return { ok: false, erro: e instanceof Error ? e.message : 'Falha de rede' }
  }
}

/** Rótulo curto do tier para o badge da mensagem. */
export function rotuloTier(tier?: number): string | null {
  if (tier === 1) return '⚡ Opus 5 (ultra)'
  if (tier === 2) return '✦ Fable 5 (máximo)'
  return null
}
